import type { ReactNode } from "react";
import { pickerSummary, selectLabel } from "../lib/selection";
import type { Query } from "../lib/query";
import { useState, useRef, useEffect, useLayoutEffect } from "react";
export function PageHead({ title, sub, actions, badge }: { title: string; sub?: string; actions?: ReactNode; badge?: ReactNode }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <div><div className="flex items-center gap-2.5"><h1 className="text-xl font-bold tracking-tight m-0">{title}</h1>{badge}</div>
        {sub && <p className="text-muted mt-0.5 mb-0">{sub}</p>}</div>
      <div className="flex-1" />{actions}
    </div>
  );
}
export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`bg-surface border border-border rounded-xl2 shadow-sm ${className}`}>{children}</div>;
}
/** TABS — the one tab strip for the whole dashboard.
 *
 *  Cut, Analytics and Integrity each hand-rolled this, with three different paddings, so the same
 *  control sat at three different heights depending which page you were on. Spacing does not drift
 *  because anyone is careless; it drifts because there is nothing to reuse. Same reason `Select`
 *  exists.
 *
 *  Deliberately SHORT (py-1.5, not py-2/py-4). A tab strip is navigation furniture sitting directly
 *  above the content people came to look at; every pixel it takes is a pixel of map or table pushed
 *  under the fold.
 *
 *  `count` renders the badge Integrity was appending into its label by hand.
 */
export function Tabs<T extends string>({ value, onChange, options, className = "" }: {
  value: T;
  onChange: (v: T) => void;
  options: ReadonlyArray<readonly [T, string] | readonly [T, string, number | null | undefined]>;
  className?: string;
}) {
  return (
    // overflow-y-hidden is NOT decorative. Per the CSS overflow spec, when ONE axis is set to
    // anything other than `visible`, a `visible` on the other axis computes to `auto` — so
    // `overflow-x-auto` alone silently makes this a VERTICAL scroll container too. The active tab
    // uses `border-b-2 -mb-px` to sit its border over the strip's, which overflows the box by a
    // pixel or two, and Windows renders that as a stubby scrollbar with arrows floating beside the
    // tabs. Pinning the y axis is the fix; without it every tab strip in the app grows one.
    <div role="tablist" className={`flex items-center gap-1 border-b border-border mb-2.5 overflow-x-auto overflow-y-hidden ${className}`}>
      {options.map((o) => {
        const [k, label] = o;
        const count = o[2];
        const on = value === k;
        return (
          <button key={k} role="tab" aria-selected={on} onClick={() => onChange(k)}
            className={`px-3 py-1.5 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${on ? "border-accent text-text" : "border-transparent text-muted hover:text-text"}`}>
            {label}
            {count ? <span className="ml-1.5 text-2xs font-bold text-muted">{count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

/** SECTIONBAR — the controls belonging to the section you are looking at.
 *
 *  THE COMPANION TO `Tabs`, AND IT EXISTS BECAUSE OF THE RULE Tabs SET.
 *  `2026-10-06-tabs-are-sections.md`: one page, one title, and the title names the page. A section
 *  under a tab therefore has no header of its own to hang a crew picker or a date range off -- and
 *  it still has a crew picker and a date range.
 *
 *  The alternative was handing each section's controls UP into the page's `PageHead actions` slot,
 *  which is where they look best and is wrong here anyway: Analytics keeps every tab it has opened
 *  MOUNTED so the map survives a tab switch, so six sections would be writing to one slot and the
 *  hidden five would win it on any re-render. Controls that live inside their own section's `hidden`
 *  wrapper leave with it. The keep-mounted trick is what forces this shape, so it is written down
 *  here rather than rediscovered.
 *
 *  One component rather than six hand-rolled rows for the same reason `Tabs` and `Select` exist:
 *  spacing does not drift because anyone is careless, it drifts because there is nothing to reuse.
 *
 *  `items-end` because the things that go in here are `Select`s and `DateRange`s that carry a small
 *  label above the control -- aligning on the top edge staggers their boxes. `badge` is the left
 *  slot, for a chip that qualifies the numbers below rather than changing them.
 *
 *  THE PORTAL HAS NO TABS AND NO USE FOR THIS, and it has it anyway: ui.tsx is a byte-identical
 *  COPY in dashboard/ and portal/ (ci.yml diffs them), because the two apps build separately and
 *  "keep them in step" has to be enforced rather than remembered. Adding a component to one and
 *  not the other is the drift that check exists to catch, and it caught this one.
 */
export function SectionBar({ children, badge, className = "" }: {
  children?: ReactNode; badge?: ReactNode; className?: string;
}) {
  return (
    <div className={`flex items-end gap-2 mb-3 flex-wrap ${className}`}>
      {badge}
      <div className="flex-1" />
      {children}
    </div>
  );
}

/** STATROW — the day's headline numbers, as ONE strip rather than a row of boxes.
 *
 *  These were five separate `Card`s, each spending a full card's height and padding to show a single
 *  integer. Stacked with the page title and the tab strip, they pushed the Live Results map off the
 *  bottom of the screen — you had to scroll to use the thing the page exists for. A number does not
 *  need a box around it to read as a number; the dividers do that work at a fraction of the height.
 *
 *  Wraps rather than scrolls on narrow screens, so nothing is ever hidden — a metric you cannot see
 *  is worse than a metric that moved to a second line.
 */
export function StatRow({ items, className = "" }: {
  items: Array<{ label: string; value: ReactNode; sub?: string; title?: string }>;
  className?: string;
}) {
  if (!items.length) return null;
  return (
    <div className={`flex flex-wrap items-stretch bg-surface border border-border rounded-xl2 shadow-sm mb-2.5 ${className}`}>
      {items.map((it, i) => (
        <div key={it.label} title={it.title}
          className={`px-3.5 py-2 min-w-[116px] flex-1 ${i ? "border-l border-border" : ""}`}>
          <div className="text-3xs font-bold text-muted uppercase tracking-wide leading-none">{it.label}</div>
          <div className="text-lg font-extrabold leading-tight mt-1">{it.value}</div>
          {it.sub && <div className="text-2xs text-muted leading-tight">{it.sub}</div>}
        </div>
      ))}
    </div>
  );
}

export function Btn({ children, variant = "solid", className = "", ...p }: any) {
  // A DISABLED BUTTON LOOKS DISABLED (2026-10-23): "Buy with card" sat bright red and did nothing
  // until the terms box was ticked. Faded, and the cursor says so.
  const base = "inline-flex items-center gap-2 rounded-lg px-3.5 py-2 font-bold text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed";
  const cls = variant === "ghost" ? "bg-surface text-text border border-border" : "bg-accent text-white border-0";
  return <button className={`${base} ${cls} ${className}`} {...p}>{children}</button>;
}

// ── A FILE PICKER THAT LOOKS LIKE A BUTTON (2026-11-01) ─────────────────────────────────────────
// Caleb: "its really hard to tell that the choose file button is in fact a button." The browser's
// own control reads as a sentence ("Choose File  No file chosen"). This is ONE line that changes
// as it goes, never two: "＋ Choose a file" with the formats beside it -> "Reading…" (dimmed) with
// the file's name and what is happening -> "Choose another file" with the name and the result.
// Every file picker in the dashboard and the portal is this component.
export function FileButton({ onPick, accept, multiple = false, busy = false, file, note, formats,
  label = "＋ Choose a file", again = "Choose another file", busyLabel = "Reading…",
  disabled = false, className = "" }: {
  onPick: (files: File[]) => void; accept?: string; multiple?: boolean;
  /** Reading or checking right now: the button dims and says busyLabel. */
  busy?: boolean;
  /** The chosen file's name, once there is one. */
  file?: string | null;
  /** Beside the name: what is happening, or what came of it. */
  note?: ReactNode;
  /** Beside the button before anything is chosen, e.g. "PDF, Excel or CSV". */
  formats?: ReactNode;
  label?: string; again?: string; busyLabel?: string; disabled?: boolean; className?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <span className={`inline-flex items-center gap-2.5 flex-wrap ${className}`}>
      <input ref={ref} type="file" accept={accept} multiple={multiple} className="hidden"
        onChange={(e) => {
          const fs = Array.from(e.target.files ?? []);
          e.target.value = "";            // the same file chosen twice still counts as a choice
          if (fs.length) onPick(fs);
        }} />
      <button type="button" onClick={() => ref.current?.click()} disabled={disabled || busy}
        className={`inline-flex items-center gap-1.5 min-h-10 px-4 rounded-lg border-2 border-accent bg-surface text-accent font-bold text-sm cursor-pointer disabled:cursor-not-allowed ${busy ? "opacity-60" : "disabled:opacity-50"}`}>
        {busy ? busyLabel : file ? again : label}
      </button>
      {file
        ? <span className="text-sm"><b>{file}</b>{note != null && note !== "" && <span className="text-muted"> · {note}</span>}</span>
        : formats != null && <span className="text-sm text-muted">{formats}</span>}
    </span>
  );
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 p-8 text-muted text-sm">
      <span className="inline-block w-4 h-4 rounded-full border-2 border-border border-t-accent animate-spin" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export function Empty({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1.5 p-10 text-center">
      <div className="text-sm font-semibold">{title}</div>
      {hint && <div className="text-xs text-muted max-w-md">{hint}</div>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/** A NUMBER YOU DRAG, WITH THE VALUE SHOWN. Lived in Cut.tsx until the sign cutter wanted the same
 *  control for the same question -- how many books -- and copying it would have been the third copy
 *  of a thing this kit exists to hold once. Caleb: "we already have a certain amount of
 *  infrastructure for this for cutting walkbooks and you need to borrow what makes sense." */
export function Slider({ label, value, setValue, min, max, hint }: {
  label: string; value: number; setValue: (n: number) => void; min: number; max: number; hint: string;
}) {
  // THE NUMBER IS TYPEABLE, AND THE SLIDER FOLLOWS IT (Caleb, 2026-09-24: "I actually like the
  // concept of the sliders ... it gives people a reference for the range that they should be
  // shooting for ... but one thing we would definitely want to allow for is to enter a custom
  // number, because sometimes it's hard to get that slider to the right number and you just want
  // to type the number that you want and have the slider move to that").
  //
  // So the read-only figure on the right becomes the input. The slider keeps doing the thing it is
  // good at -- showing the range somebody should be aiming inside -- and stops being the only way
  // to land on 17.
  //
  // TYPING IS CLAMPED ON COMMIT, NOT PER KEYSTROKE. Clamping as you type makes "10" impossible to
  // reach from "1" when the minimum is 5: the 1 snaps to 5 before the 0 arrives. So the field holds
  // whatever is typed and settles on blur or Enter.
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    const n = parseInt(draft, 10);
    setValue(Number.isFinite(n) ? Math.max(min, Math.min(max, n)) : value);
    setDraft(null);
  };
  return (
    <div className="mb-4">
      <div className="flex justify-between items-center font-bold text-sm mb-1.5">
        <span>{label}</span>
        <input type="number" inputMode="numeric" min={min} max={max}
               aria-label={label}
               value={draft ?? String(value)}
               onChange={(e) => setDraft(e.target.value)}
               onBlur={commit}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
               className="w-16 text-right bg-surface-2 border border-border rounded-md px-2 py-0.5 text-accent font-bold outline-none focus:border-accent" />
      </div>
      <input type="range" min={min} max={max} value={value} onChange={(e) => { setDraft(null); setValue(+e.target.value); }} className="w-full accent-[var(--accent)]" />
      <div className="text-2xs text-muted mt-1">{hint}</div>
    </div>
  );
}

// Shimmer placeholder for content that's loading (use for lists/cards instead of a blank panel).
export function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse rounded bg-surface-2 ${className}`} aria-hidden="true" />;
}

// Compact inline "refreshing" indicator — for a list that already has rows and is re-fetching,
// so a filter/date change isn't silent (distinct from the full-panel <Loading /> on first load).
// ── List panels ─────────────────────────────────────────────────────────────────────────────────
// Rendering is driven by Query<T> (lib/query.ts), a discriminated union where `data` does not exist
// until the query has actually loaded — so the old bug (a `loading` boolean defaulting to false,
// with the empty state keyed off rows.length === 0) cannot be written without a type error.
// ── ONE WAY TO SAY SOMETHING FAILED (2026-10-30) ───────────────────────────────────────────────
// Caleb: errors were "too subtle" -- grey or amber text in a corner, far from the click, reading
// `API 400: {"detail": ...}`. Then, on the first mockup's red boxes: "way too much real estate ...
// It just needs to be like a simple red error message", with "Try again" only where it adds value.
// So: one line of red text, placed where the thing failed. The words come from lib/plainError.ts.
// Pass onRetry only when trying again can work -- a dropped connection or a fault on our end
// (`canRetry(e)`); a refusal fails the same way every time. `inline` for a spot inside a row.
export function ErrorLine({ message, onRetry, inline = false, className = "" }: {
  message?: string | null; onRetry?: () => void; inline?: boolean; className?: string;
}) {
  if (!message) return null;
  const Tag = inline ? "span" : "p";
  return (
    <Tag role="alert" className={`${inline ? "" : "m-0 "}text-sm font-semibold leading-snug ${className}`} style={{ color: "var(--bad)" }}>
      {message}
      {onRetry && (
        <button type="button" onClick={onRetry} className="ml-1.5 font-bold underline" style={{ color: "var(--bad)" }}>
          Try again
        </button>
      )}
    </Tag>
  );
}

export function ErrorPanel({ message, onRetry }: { message?: string | null; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1.5 p-10 text-center">
      <div className="text-sm font-semibold" style={{ color: "var(--bad)" }}>Couldn't load this list</div>
      {message && <div className="text-sm max-w-md break-words" style={{ color: "var(--bad)" }}>{message}</div>}
      {onRetry && (
        <button type="button" onClick={onRetry} className="mt-1 text-sm font-bold underline" style={{ color: "var(--bad)" }}>
          Try again
        </button>
      )}
    </div>
  );
}

/**
 * Renders exactly one of: spinner, error, empty, or the list — from a Query.
 *
 * `children` is a RENDER FUNCTION taking the loaded data. That is what makes the guarantee real:
 * there is no path to your table markup without the union having narrowed to a state that HAS data.
 * A list that already has rows keeps rendering them while refreshing (with a small strip on top), so
 * a filter change never blanks the table — we are not trading a flash of "no results" for a flash of
 * nothing.
 */
export function ListPanel<T>({
  query, isEmpty, empty, idle, onRetry, children,
}: {
  query: Query<T>;
  isEmpty: (data: T) => boolean;
  empty: ReactNode;
  /** Shown before anything has been requested (explicit-search pages). Defaults to `empty`. */
  idle?: ReactNode;
  onRetry?: () => void;
  children: (data: T) => ReactNode;
}) {
  if (query.status === "idle") return <>{idle ?? empty}</>;
  if (query.status === "loading") return <Loading />;
  if (query.status === "error" && query.data === undefined) {
    return <ErrorPanel message={query.error} onRetry={onRetry} />;
  }
  const data = query.data as T;
  return (
    <>
      {query.status === "refreshing" && (
        <div className="px-3 py-1.5 border-b border-border"><Refreshing /></div>
      )}
      {query.status === "error" && (
        <div className="px-3 py-1.5 border-b border-border">
          <ErrorLine message={`Couldn't refresh, so these are the last results. ${query.error ?? ""}`.trim()} onRetry={onRetry} />
        </div>
      )}
      {isEmpty(data) ? empty : children(data)}
    </>
  );
}

export function Refreshing({ label = "Refreshing…" }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted" role="status" aria-live="polite">
      <span className="inline-block w-3 h-3 rounded-full border-2 border-border border-t-accent animate-spin" aria-hidden="true" />
      {label}
    </span>
  );
}

export function StatusToggle({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <div className="flex rounded-lg overflow-hidden border border-border text-xs shrink-0">
      {options.map(([val, label]) => (
        <button key={val} type="button" onClick={() => onChange(val)}
          className={`px-3 py-1.5 font-semibold ${value === val ? "bg-accent text-white" : "text-muted hover:text-text hover:bg-surface-2"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}


// Single select control for the whole dashboard. Native <select> was dropped because its CLOSED
// field cannot be styled or sized reliably — every call site had ended up hard-capping width
// (max-w-[180px]) which chopped real option text ("2026 General ID Survey"). Here the closed button
// ellipsises and carries the full text as its tooltip, while the open list stays unwrapped, so no
// label is ever unreadable.
//
// Keyboard behaviour is implemented deliberately: this replaces a native control, and a div-based
// listbox that cannot be driven from the keyboard is a regression, not a redesign. Supported:
// Enter/Space/ArrowDown/ArrowUp to open, arrows + Home/End to move, type-ahead to jump, Enter to
// commit, Escape to cancel and return focus, Tab to close.
// A LIST LONGER THAN THIS GETS A SEARCH BOX (2026-09-22). Caleb: "when you have this many options it
// has to be something you can scroll and search" -- 254 Texas counties, a campaign's volunteers, its
// tags. Short lists (party, status, gender) stay a plain list: a search box over three rows is noise.
export const SEARCH_FROM = 8;

// ── A LIST OPENS WHERE THERE IS ROOM (2026-10-11) ─────────────────────────────────────────────
//
// Caleb, on the tag picker of a survey's LAST answer: it "is pushing the drop down menu like down and
// so like that creates like extra scrolling space". Every popover here hung below its button
// unconditionally, so near the bottom of the page it extended the page instead of fitting on it.
//
// Measured at the moment it opens: if the space below the button cannot hold the list and there is
// more room above, it opens UPWARD; either way its height is capped to the room on the side it
// opens, so it never grows the page. One rule for every dropdown, not a fix for one screen.
export function useDropPlacement(open: boolean, anchor: React.RefObject<HTMLElement | null>, want = 320) {
  const [place, setPlace] = useState<{ up: boolean; room: number }>({ up: false, room: want });
  useLayoutEffect(() => {
    if (!open || !anchor.current) return;
    const r = anchor.current.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - 12;
    const above = r.top - 12;
    const up = below < want && above > below;
    setPlace({ up, room: Math.max(120, Math.floor(up ? above : below)) });
  }, [open, anchor, want]);
  return place;
}

// ── A LIST IS NEVER CUT OFF BY THE BOX IT SITS IN (2026-11-01) ───────────────────────────────────
//
// Caleb, on the voter import's field mapping and the poll-greeting plan's "which column is which":
// clicking a dropdown inside those panels showed a list "cut off -- I can't scroll, it doesn't
// provide all the options". Both panels scroll on their own, and a list hung `absolute` inside a
// scrolling box is clipped by that box: the room useDropPlacement measured was the WINDOW's, but the
// box was smaller. So an open list is now `position: fixed` to the button -- out of every scrolling
// box -- placed by the same rule (below if it fits, above if there is more room there, never taller
// than the room) and kept on the button while anything scrolls or the window resizes.
//
// IN PLACE, NOT A PORTAL, ON PURPOSE. The list stays inside its picker in the page, so every popover
// and modal around it that closes on "a click outside me" still counts a click in the list as
// inside. The one thing a fixed box cannot assume is that it is fixed to the window: an ancestor
// with a transform or a filter moves its origin. So the first placement is measured, and any gap
// between where it was put and where it landed is taken back out.
export function useFloatingList(open: boolean, anchor: React.RefObject<HTMLElement | null>,
                                list: React.RefObject<HTMLElement | null>, want = 320, matchWidth = false) {
  const [style, setStyle] = useState<React.CSSProperties>({ position: "fixed", visibility: "hidden" });
  const shift = useRef({ x: 0, y: 0 });
  useLayoutEffect(() => {
    if (!open) return;
    shift.current = { x: 0, y: 0 };
    // Where it should sit, in window coordinates.
    const want_ = () => {
      const a = anchor.current;
      if (!a) return null;
      const r = a.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - 12;
      const above = r.top - 12;
      const up = below < want && above > below;
      const room = Math.max(120, Math.floor(up ? above : below));
      // Kept on the screen sideways too: a wide list under a button near the right edge.
      const w = list.current?.offsetWidth ?? 0;
      const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
      return { up, room, left, top: r.bottom + 4, bottom: r.top - 4, width: r.width };
    };
    const place = () => {
      const p = want_();
      if (!p) return;
      // An upward list is pinned by its bottom edge so it grows up from the button.
      // A list as wide as its field ("w-full") is given the field's width in pixels: once the list is
      // fixed, a percentage width means the WINDOW's, and the precinct list on Create walkbooks ran
      // the full width of the screen (2026-11-02).
      setStyle({ position: "fixed", left: p.left - shift.current.x, minWidth: p.width, maxHeight: p.room, zIndex: 60,
                 ...(matchWidth ? { width: p.width } : {}),
                 ...(p.up ? { bottom: window.innerHeight - p.bottom - shift.current.y } : { top: p.top - shift.current.y }) });
    };
    place();
    const again = () => place();
    window.addEventListener("scroll", again, true);
    window.addEventListener("resize", again);
    // After paint: measure where it landed and take any ancestor's offset out, then place it again
    // now that it has a width to keep on screen.
    const raf = requestAnimationFrame(() => {
      const el = list.current, p = want_();
      if (el && p) {
        const got = el.getBoundingClientRect();
        shift.current = { x: got.left - p.left, y: p.up ? p.bottom - got.bottom : got.top - p.top };
      }
      place();
    });
    return () => { window.removeEventListener("scroll", again, true); window.removeEventListener("resize", again); cancelAnimationFrame(raf); };
  }, [open, anchor, list, want, matchWidth]);
  return style;
}

export function Select({ value, onChange, options, className = "", size = "md", hint, width = "auto",
                        variant = "control", disabled = false, on = false }:
  { value: string; onChange: (v: string) => void; options: [string, string][]; className?: string;
    size?: "sm" | "md"; hint?: string; width?: "auto" | "full";
    variant?: "control" | "bare"; disabled?: boolean;
    /** a FILTER that is set: accent border, so a narrowed list says so at a glance (Walkbooks row) */
    on?: boolean }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);          // index into `view`
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const typed = useRef<{ s: string; t: number }>({ s: "", t: 0 });
  const uid = useRef("sel" + Math.random().toString(36).slice(2, 8)).current;

  const searchable = options.length > SEARCH_FROM;
  // What the open list shows: every option, or the ones matching the search.
  const view = searchable && q.trim()
    ? options.filter(([, l]) => l.toLowerCase().includes(q.trim().toLowerCase()))
    : options;
  const idx = view.findIndex(([v]) => v === value);
  // A PICKER THAT CANNOT SAY WHAT IS SELECTED MUST SAY SO. See selectLabel: falling back to
  // options[0] made a stale selection LOOK like a valid one, which is how a campaign switch put
  // another campaign's universe name over an empty map.
  const label = selectLabel(value, options);
  const float = useFloatingList(open, btnRef, popRef, searchable ? 340 : 296);

  const close = () => { setOpen(false); setQ(""); };
  useEffect(() => {
    const h = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current && !ref.current.contains(t) && !popRef.current?.contains(t)) close();
    };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => { if (open) setActive(idx >= 0 ? idx : 0); }, [open, q]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {   // keep the highlighted row in view when arrowing past the fold
    if (!open || active < 0) return;
    listRef.current?.querySelector<HTMLElement>(`#${uid}-o${active}`)?.scrollIntoView({ block: "nearest" });
  }, [open, active, uid]);

  const commit = (i: number) => { const o = view[i]; if (o) onChange(o[0]); close(); btnRef.current?.focus(); };

  const jump = (ch: string) => {   // type-ahead: same as a native select
    const now = Date.now();
    typed.current.s = now - typed.current.t > 700 ? ch : typed.current.s + ch;
    typed.current.t = now;
    const qq = typed.current.s.toLowerCase();
    const from = active < 0 ? 0 : active;
    const order = [...view.slice(from + 1), ...view.slice(0, from + 1)];
    const hit = order.find(([, l]) => l.toLowerCase().startsWith(qq));
    if (hit) { const i = view.indexOf(hit); open ? setActive(i) : commit(i); }
  };

  // Keys shared by the button and the search box: move, commit, cancel.
  const onListKey = (e: React.KeyboardEvent, fromSearch: boolean) => {
    const k = e.key;
    if (k === "Escape") { e.preventDefault(); close(); btnRef.current?.focus(); }
    else if (k === "Tab") close();
    else if (k === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(view.length - 1, a + 1)); }
    else if (k === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (k === "Home" && !fromSearch) { e.preventDefault(); setActive(0); }
    else if (k === "End" && !fromSearch) { e.preventDefault(); setActive(view.length - 1); }
    else if (k === "Enter" || (k === " " && !fromSearch)) { e.preventDefault(); commit(active); }
    else if (!fromSearch && k.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) { e.preventDefault(); jump(k); }
  };
  const onKey = (e: React.KeyboardEvent) => {
    const k = e.key;
    if (!open) {
      if (k === "Enter" || k === " " || k === "ArrowDown" || k === "ArrowUp") { e.preventDefault(); setOpen(true); }
      else if (k.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        // A searchable list opens straight into its search box with the letter typed.
        if (searchable) { setQ(k); setOpen(true); } else jump(k);
      }
      return;
    }
    onListKey(e, false);
  };

  const pad = size === "sm" ? "h-7 px-2 text-xs" : "h-8 px-3 text-sm";
  // "bare" keeps the header campaign switcher and other inline dropdowns looking like text.
  const skin = variant === "bare"
    ? "bg-transparent font-bold cursor-pointer"
    : `rounded-lg border ${on ? "border-accent" : "border-border"} bg-surface-2 ${pad}`;
  // Grow to the content but never past the row; the tooltip and the open list carry the full text.
  const w = width === "full" ? "w-full" : "max-w-full";
  return (
    <div ref={ref} className={`relative ${width === "full" ? "block" : "inline-block"} min-w-0 ${className}`}>
      <button ref={btnRef} type="button" disabled={disabled} onClick={() => (open ? close() : setOpen(true))} onKeyDown={onKey}
        title={label || undefined} aria-label={hint} aria-haspopup="listbox" aria-expanded={open}
        aria-controls={open ? `${uid}-list` : undefined}
        aria-activedescendant={open && active >= 0 ? `${uid}-o${active}` : undefined}
        className={`inline-flex items-center gap-2 text-text disabled:opacity-60 ${skin} ${w} ${width === "full" ? "justify-between" : ""}`}>
        <span className="truncate min-w-0">{label}</span><span className="opacity-60 text-xs shrink-0">▾</span>
      </button>
      {open && (
        <div ref={popRef} className="max-w-[min(90vw,28rem)] rounded-lg border border-border bg-surface shadow-lg flex flex-col text-text"
          style={float}>
          {searchable && (
            <div className="p-2 border-b border-border">
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => onListKey(e, true)}
                placeholder={`Search ${options.length}…`} aria-label={`Search ${hint || "options"}`} name="option-search"
                className="ctl !h-7 text-xs w-full" />
            </div>
          )}
          <div ref={listRef} id={`${uid}-list`} role="listbox" aria-label={hint} className="max-h-72 min-h-0 overflow-auto py-1">
            {view.length === 0 && <div className="px-3 py-2 text-xs text-muted">No matches</div>}
            {view.map(([v, l], i) => (
              <button key={v} id={`${uid}-o${i}`} role="option" aria-selected={v === value} type="button" tabIndex={-1}
                onClick={() => commit(i)} onMouseEnter={() => setActive(i)}
                className={`block w-full text-left px-3 py-1.5 text-sm whitespace-nowrap ${i === active ? "bg-surface-2" : ""} ${v === value ? "text-accent font-semibold" : ""}`}>{l}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// Searchable multi-select -- a checklist popover that scales to many options (254 counties, a
// campaign's precincts, its tags and its volunteers). Search once the list is longer than
// SEARCH_FROM; Select all / Clear; a body that SCROLLS under a fixed ceiling rather than growing;
// and an optional "all" row above the list (e.g. "All counties, including any added later"), which
// is a different thing from ticking every row: it also covers what does not exist yet.
//
// ── ONE OF THESE, NOT FOUR (2026-09-28) ─────────────────────────────────────────────────────────
//
// Caleb, on the sign cutter's precinct picker -- an unbounded column of checkboxes running off the
// bottom of the panel: "this should be a list with a scrolling option after a certain number, and
// we have this regularly for lengthy lists for filters. This is another candidate for
// standardization in the system."
//
// He was right that it is regular. There were FOUR versions of this one idea: this one, a private
// copy in the walkbook cutter for precincts, another beside it for tag gates, and -- on the sign
// cutter -- no control at all, just the raw list. Only the ones that reused something knew how to
// scroll. The other three are gone, and the props they each needed live here now:
//
//   * `size` -- "sm" for a picker that has to sit inline in a filter row, like Select's own.
//   * `disabled` + `disabledText` -- a picker whose answer depends on a field above it says "pick
//     a county" rather than opening onto an empty list and leaving you to work out why.
//   * the .orator-map exception in the click-away below.
//   * `sub`, `chips` and `footer` (2026-10-15) -- the "add several people" picker (AddMany): a
//     muted note on a row ("Team Greiss", "moves from Northside"), one-tap chips that tick a whole
//     team, and a footer for the one button that applies what is ticked.
export function MultiSelect({ options, selected, onChange, label, className = "", all, noun,
                              width = "w-64", size = "md", disabled = false, disabledText, sub, chips, footer, look = "field" }:
  { options: [string, string][]; selected: string[]; onChange: (next: string[]) => void; label: string; className?: string;
    all?: { label: string; hint?: string; on: boolean; onChange: (on: boolean) => void };
    noun?: [string, string]; width?: string; size?: "sm" | "md";
    disabled?: boolean; disabledText?: string;
    sub?: Record<string, string | null | undefined>;
    chips?: { label: string; values: string[] }[];
    footer?: (close: () => void) => ReactNode;
    /** "action": the trigger reads as a button that DOES something (＋ Add people), not a filter
     *  field (2026-10-15: Caleb could not see the add pickers, which looked like empty inputs).
     *  "change": a small Change button beside text the caller shows itself -- the Assignee column
     *  (2026-10-29: "I also need to be able to see who a call list is assigned to without having to
     *  use a drop down"). The names stay readable; the button opens the same picker. */
    look?: "field" | "action" | "change" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  // "w-full" on a fixed list would be the window's width; the field's is what it means.
  const fill = /(^|\s)w-full(\s|$)/.test(width);
  const float = useFloatingList(open, ref, popRef, 340, fill);
  useEffect(() => {
    const h = (e: MouseEvent) => {
        if (!ref.current || ref.current.contains(e.target as Node) || popRef.current?.contains(e.target as Node)) return;
      // CLICKING THE MAP DOES NOT CLOSE THIS. On a cutting screen the map is the OTHER way to pick
      // the same precincts, so a list that shut the moment you reached for the map would make the
      // two controls fight each other. Everywhere else there is no .orator-map under the cursor and
      // this is the ordinary click-away.
      if ((e.target as HTMLElement)?.closest?.(".orator-map")) return;
      setOpen(false); setQ("");
    };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  const searchable = options.length > SEARCH_FROM;
  const filtered = searchable && q.trim() ? options.filter(([, l]) => l.toLowerCase().includes(q.trim().toLowerCase())) : options;
  const toggle = (v: string) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  // SAY WHAT IS PICKED, not just how many, while that still fits -- pickerSummary in lib/selection,
  // shared by name with the phone's multi-picker rather than written out twice.
  const names = options.filter(([v]) => selected.includes(v)).map(([, l]) => l);
  const summary = disabled ? (disabledText ?? label)
    : all?.on ? all.label
    : pickerSummary(names, label, noun);
  const box = size === "sm" ? "h-7 px-2 text-xs" : "h-8 px-3 text-sm";
  return (
    <div ref={ref} className={`relative ${className}`}>
      <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)}
        title={disabled ? (disabledText ?? label) : all?.on ? all.label : names.join(", ") || undefined}
        className={look === "action"
          ? `inline-flex items-center gap-2 whitespace-nowrap rounded-lg border border-accent text-accent font-semibold hover:bg-accent hover:text-white disabled:opacity-60 disabled:cursor-not-allowed ${box}`
          : look === "change"
          ? "inline-flex items-center whitespace-nowrap rounded-md border border-border bg-surface px-2 py-0.5 text-xs font-semibold text-muted hover:text-text hover:border-accent disabled:opacity-60 disabled:cursor-not-allowed"
          : `inline-flex items-center gap-2 whitespace-nowrap rounded-lg border border-border bg-surface-2 text-text w-full justify-between disabled:opacity-60 disabled:cursor-not-allowed ${box}`}>
        {look === "change"
          ? <span>Change</span>
          : look === "action"
          ? <span className="truncate">＋ {summary}</span>
          : <><span className={`truncate ${disabled || (!all?.on && names.length === 0) ? "text-muted" : ""}`}>{summary}</span><span className="opacity-60 text-xs">▾</span></>}
      </button>
      {open && !disabled && (
        <div ref={popRef} className={`${fill ? width.replace(/(^|\s)w-full(?=\s|$)/, " ").trim() : width} max-w-[90vw] rounded-lg border border-border bg-surface shadow-lg flex flex-col text-text`}
          style={float}>
          {all && (
            <label className="flex items-start gap-2 px-3 py-2 border-b border-border text-sm cursor-pointer hover:bg-surface-2">
              <input type="checkbox" checked={all.on} onChange={(e) => all.onChange(e.target.checked)} className="mt-0.5" />
              <span><span className="font-semibold">{all.label}</span>{all.hint && <span className="block text-xs text-muted">{all.hint}</span>}</span>
            </label>
          )}
          {!all?.on && (<>
            {searchable && (
              <div className="p-2 border-b border-border"><input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${options.length}…`} aria-label="Filter options" name="option-filter" className="ctl !h-7 text-xs" /></div>
            )}
            {chips && chips.length > 0 && (
              <div className="flex flex-wrap gap-1.5 px-3 py-2 border-b border-border">
                {chips.map((c) => (
                  <button key={c.label} type="button" onClick={() => onChange([...new Set([...selected, ...c.values])])}
                    className="px-2 py-0.5 rounded-full border border-border text-xs hover:border-accent hover:text-accent">{c.label}</button>
                ))}
              </div>
            )}
            {options.length > 2 && (
              <div className="flex items-center justify-between px-3 py-1.5 border-b border-border text-xs">
                <button type="button" onClick={() => onChange([...new Set([...selected, ...filtered.map(([v]) => v)])])} className="text-accent hover:underline">
                  {q.trim() ? `Select these ${filtered.length}` : `Select all ${options.length}`}
                </button>
                {selected.length > 0 && <button type="button" onClick={() => onChange([])} className="text-muted hover:text-warn">Clear</button>}
              </div>
            )}
            {/* THE CEILING IS THE POINT. 254 counties, or a county's 800 precincts, is a list that
                has to be scrolled and searched rather than rendered down the page and off it. */}
            <div className="max-h-56 min-h-0 overflow-auto py-1">
              {options.length === 0 ? <div className="px-3 py-2 text-xs text-muted">Nothing to pick</div>
                : filtered.length === 0 ? <div className="px-3 py-2 text-xs text-muted">No matches</div>
                : filtered.map(([v, l]) => (
                  <button key={v} type="button" onClick={() => toggle(v)} className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-sm hover:bg-surface-2">
                    <span className={`w-3.5 h-3.5 rounded border grid place-items-center text-3xs text-white shrink-0 ${selected.includes(v) ? "bg-accent border-accent" : "border-border"}`}>{selected.includes(v) ? "✓" : ""}</span>
                    <span className="truncate">{l}</span>
                    {sub?.[v] && <span className="ml-auto pl-2 text-xs text-muted truncate shrink-0 max-w-[45%]">{sub[v]}</span>}
                  </button>
                ))}
            </div>
            {footer?.(() => { setOpen(false); setQ(""); })}
          </>)}
        </div>
      )}
    </div>
  );
}
